import React from "react";
import useInvoices, { type InvoiceSummary } from "@/components/context/useInvoices";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";

function formatAmount(totalSmallestUnit: number, currency: string): string {
  const amount = totalSmallestUnit / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency.toUpperCase()}`;
  }
}

function formatDate(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleDateString();
}

function statusVariant(status: InvoiceSummary["status"]): "default" | "secondary" | "destructive" {
  switch (status) {
    case "paid":
      return "default";
    case "open":
      return "secondary";
    default:
      return "destructive";
  }
}

export default function RecentInvoicesCard(): React.JSX.Element {
  const { invoices, isLoading, isError } = useInvoices();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent invoices</CardTitle>
        <CardDescription>Your last 12 invoices from Stripe.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : isError ? (
          <p className="text-sm text-red-600">Could not load invoices. Try again later.</p>
        ) : invoices.length === 0 ? (
          <p className="text-sm text-muted-foreground">No invoices yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 pr-4 font-medium">Number</th>
                  <th className="py-2 pr-4 font-medium">Amount</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b last:border-b-0">
                    <td className="py-2 pr-4">{formatDate(inv.created)}</td>
                    <td className="py-2 pr-4">{inv.number ?? "—"}</td>
                    <td className="py-2 pr-4">{formatAmount(inv.total, inv.currency)}</td>
                    <td className="py-2 pr-4">
                      <Badge variant={statusVariant(inv.status)}>{inv.status}</Badge>
                    </td>
                    <td className="py-2 space-x-3">
                      {inv.hosted_invoice_url && (
                        <a
                          href={inv.hosted_invoice_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline cursor-pointer"
                        >
                          View
                        </a>
                      )}
                      {inv.invoice_pdf && (
                        <a
                          href={inv.invoice_pdf}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline cursor-pointer"
                        >
                          PDF
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
